<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { isCancel } from "axios"
import { computed, inject, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useToast } from "primevue/usetoast"
import { ServiceError } from "@core/exceptions"
import type { Services } from "@services"
import { useConfigStore } from "@core/stores/config.store"
import { useAlarmeStore } from "@modules/energy-manager/stores/alarme.store"
import type { ThresholdPoint } from "@modules/energy-manager/types/alarme"
import {
    getDefaultAlarmThresholdFreqForVariable,
    getDistinctAlarmThresholdFreqsFromProfils,
    type AlarmThresholdVariableOption,
} from "@modules/energy-manager/utils/alarmThresholdVariablesOptions"

defineOptions({
    name: "AlarmBulkGenerateDialog",
})

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            alarmBulk: {
                title: "Générer des alarmes",
                intro: "Cochez les variables pour lesquelles vous souhaitez calculer un profil automatique à partir des données disponibles. Lorsque les deux fréquences sont possibles, choisissez horaire ou quotidien avant de lancer la génération.",
                selectAll: "Tout sélectionner",
                selectNone: "Tout désélectionner",
                generate: "Générer",
                backToSelection: "Modifier la sélection",
                failuresTitle: "Variables sans profil généré",
                failuresHint:
                    "Vous pouvez créer un seuil manuellement pour ces variables depuis « Ajouter une alarme ».",
                columnVariable: "Variable",
                columnReason: "Motif",
                successesTitle: "Profils prêts à être ajoutés",
                addGenerated: "Ajouter les alarmes générées",
                close: "Fermer",
                cancel: "Annuler",
                freqHourly: "Horaire",
                freqDaily: "Quotidien",
                noProfilConfig:
                    "Aucun profil horaire ou quotidien configuré pour cette variable.",
                errorGenerate: "La génération du profil a échoué.",
                invalidVariableNoData:
                    "Pas assez de données pour cette variable.",
                persistSuccess: "{count} alarme(s) ajoutée(s).",
                persistError: "L’enregistrement d’une alarme a échoué.",
            },
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
type DialogStep = "select" | "review"

type GeneratedSuccessRow = {
    variable: string
    displayName: string
    freq: "hourly" | "daily"
    points: ThresholdPoint[]
}

type GeneratedFailureRow = {
    variable: string
    displayName: string
    reason: string
}

/** État d’avancement par variable pendant la génération (liste à coches). */
type GenerationLineStatus = "loading" | "success" | "error"

// ================================
// PROPS & EMITS
// ================================
const props = defineProps<{
    /** Portée bâtiment ou source (même convention que l’ajout manuel de seuil). */
    type: "building" | "source"
    /** Identifiant `_id_building` ou `_id_source` selon `type`. */
    itemId: string
    /** Options variables (même liste que l’ajout manuel de seuil). */
    variablesOptions: AlarmThresholdVariableOption[]
}>()

const visible = defineModel<boolean>("visible", { required: true })

// ================================
// COMPOSABLES
// ================================
const services = inject<Services>("services")!
const configStore = useConfigStore()
const alarmeStore = useAlarmeStore()
const toast = useToast()

// ================================
// STATE
// ================================
/** Étape sélection des variables ou relecture des résultats. */
const step = ref<DialogStep>("select")
/** Cases cochées par identifiant de variable. */
const selectedByVariable = ref<Record<string, boolean>>({})
/** Fréquence seuil choisie par variable (aligné `AlarmFormAdd` quand horaire + quotidien). */
const freqByVariable = ref<Record<string, "hourly" | "daily">>({})
/** Lignes générées avec succès (points + fréquence). */
const generationSuccesses = ref<GeneratedSuccessRow[]>([])
/** Lignes en échec avec libellé utilisateur. */
const generationFailures = ref<GeneratedFailureRow[]>([])
/** Chargement de l’étape génération (appels profil en parallèle). */
const isGenerating = ref(false)
/** Enregistrement séquentiel des seuils. */
const isPersisting = ref(false)
/** Annule les GET profil pendant la génération. */
const generationAbortController = ref<AbortController | null>(null)
/** Annule POST/PUT profil & alarme pendant la persistance. */
const persistAbortController = ref<AbortController | null>(null)
/** Par variable : chargement / succès / échec pendant « Générer » (variables cochées uniquement). */
const generationLineByVariable = ref<Record<string, GenerationLineStatus>>({})

// ================================
// COMPUTED
// ================================
/** Titre de la modale. */
const getDialogHeader = computed(() => t("alarmBulk.title"))

/** Au moins une variable cochée pour lancer la génération. */
const getHasSelection = computed(() =>
    props.variablesOptions.some((o) => selectedByVariable.value[o.value])
)

/** Peut cliquer sur « Générer ». */
const getCanGenerate = computed(
    () => getHasSelection.value && !isGenerating.value && Boolean(props.itemId)
)

/** Succès à persister. */
const getHasSuccessesToPersist = computed(
    () => generationSuccesses.value.length > 0
)

// ================================
// WATCHERS
// ================================
watch(visible, (isOpen) => {
    if (!isOpen) {
        generationAbortController.value?.abort()
        persistAbortController.value?.abort()
        generationAbortController.value = null
        persistAbortController.value = null
        return
    }
    step.value = "select"
    generationSuccesses.value = []
    generationFailures.value = []
    isGenerating.value = false
    isPersisting.value = false
    const next: Record<string, boolean> = {}
    const nextFreq: Record<string, "hourly" | "daily"> = {}
    for (const o of props.variablesOptions) {
        next[o.value] = true
        const cv = configStore.getVariableByType(o.value)
        const def = getDefaultAlarmThresholdFreqForVariable(cv)
        if (def) {
            nextFreq[o.value] = def
        }
    }
    selectedByVariable.value = next
    freqByVariable.value = nextFreq
    generationLineByVariable.value = {}
})

// ================================
// BUSINESS LOGIC
// ================================
/**
 * Libellé utilisateur pour une fréquence seuil.
 *
 * @param freq horaire ou quotidien
 * @returns texte traduit
 */
function formatFreqLabel(freq: "hourly" | "daily"): string {
    return freq === "hourly"
        ? t("alarmBulk.freqHourly")
        : t("alarmBulk.freqDaily")
}

/**
 * Libellés + valeurs pour les boutons de fréquence (variable config).
 *
 * @param variableType identifiant type variable
 * @returns options affichables, ordre métier depuis les profils
 */
function getBulkFreqButtonOptions(
    variableType: string
): Array<{ label: string; value: "hourly" | "daily" }> {
    const configVar = configStore.getVariableByType(variableType)
    const freqs = getDistinctAlarmThresholdFreqsFromProfils(configVar?.profils)
    return freqs.map((value) => ({
        value,
        label: formatFreqLabel(value),
    }))
}

/**
 * Affiche le choix horaire / quotidien uniquement si les deux profils sont configurés.
 *
 * @param variableType identifiant type variable
 * @returns true si deux fréquences distinctes
 */
function getShowBulkFreqSelector(variableType: string): boolean {
    return getBulkFreqButtonOptions(variableType).length > 1
}

/**
 * Met à jour la fréquence choisie pour une ligne (génération groupée).
 *
 * @param variable identifiant type variable
 * @param freq horaire ou quotidien
 * @returns void
 */
function onBulkVariableFreq(variable: string, freq: "hourly" | "daily"): void {
    freqByVariable.value = {
        ...freqByVariable.value,
        [variable]: freq,
    }
}

/**
 * Message d’erreur lisible pour une exception de génération (aligné `AlarmFormAutoGenerate`).
 *
 * @param err erreur levée par l’API ou `EMPTY_GRAPH`
 * @returns texte pour le tableau des échecs
 */
function getGenerationFailureMessage(err: unknown): string {
    if (err instanceof Error && err.message === "EMPTY_GRAPH") {
        return t("alarmBulk.errorGenerate")
    }
    if (
        err instanceof ServiceError &&
        err.code === "invalid_variable_no_data"
    ) {
        return t("alarmBulk.invalidVariableNoData")
    }
    if (err instanceof ServiceError) {
        return err.message
    }
    return t("alarmBulk.errorGenerate")
}

/**
 * Met à jour l’état visuel d’une ligne de génération (succès / échec).
 *
 * @param variable identifiant de variable
 * @param status nouveau statut
 */
function setGenerationLineStatus(
    variable: string,
    status: GenerationLineStatus
): void {
    generationLineByVariable.value = {
        ...generationLineByVariable.value,
        [variable]: status,
    }
}

/**
 * Tente de récupérer les points de profil pour une variable (fréquence choisie dans la liste).
 *
 * @param option option liste (type + nom)
 * @param signal signal d’annulation (fermeture modale / Annuler)
 * @returns points et fréquence utilisées, ou erreur finale
 */
async function tryFetchGeneratedPoints(
    option: AlarmThresholdVariableOption,
    signal: AbortSignal
): Promise<
    | { ok: true; points: ThresholdPoint[]; freq: "hourly" | "daily" }
    | { ok: false; error: unknown }
> {
    const configVar = configStore.getVariableByType(option.value)
    const availableFreqs = getDistinctAlarmThresholdFreqsFromProfils(
        configVar?.profils
    )
    const chosen = freqByVariable.value[option.value]
    const freqToUse =
        chosen && availableFreqs.includes(chosen)
            ? chosen
            : getDefaultAlarmThresholdFreqForVariable(configVar)
    if (!freqToUse) {
        return {
            ok: false,
            error: new Error("NO_PROFIL_CONFIG"),
        }
    }
    if (signal.aborted) {
        return {
            ok: false,
            error: new DOMException("Aborted", "AbortError"),
        }
    }
    try {
        const { points } =
            props.type === "building"
                ? await services.$getBuildingProfileByData(
                      props.itemId,
                      option.value,
                      freqToUse,
                      { signal }
                  )
                : await services.$getSourceProfileByData(
                      props.itemId,
                      option.value,
                      freqToUse,
                      { signal }
                  )
        if (!points?.length) {
            return { ok: false, error: new Error("EMPTY_GRAPH") }
        }
        return { ok: true, points, freq: freqToUse }
    } catch (e) {
        if (
            isCancel(e) ||
            (e instanceof DOMException && e.name === "AbortError")
        ) {
            return { ok: false, error: e }
        }
        return { ok: false, error: e }
    }
}

// ================================
// INTERACTIONS
// ================================
/**
 * Coche ou décoche toutes les variables listées.
 *
 * @param value true pour tout sélectionner
 * @returns void
 */
function onToggleSelectAll(value: boolean): void {
    const next: Record<string, boolean> = {}
    for (const o of props.variablesOptions) {
        next[o.value] = value
    }
    selectedByVariable.value = next
}

/**
 * Met à jour la sélection pour une variable.
 *
 * @param variable identifiant type variable
 * @param checked nouvelle valeur
 * @returns void
 */
function onVariableCheck(variable: string, checked: boolean): void {
    selectedByVariable.value = {
        ...selectedByVariable.value,
        [variable]: checked,
    }
}

/**
 * Lance la génération pour chaque variable cochée (récupération des profils en parallèle).
 *
 * @returns void
 */
async function onGenerate(): Promise<void> {
    if (!getCanGenerate.value) return
    generationAbortController.value?.abort()
    const controller = new AbortController()
    generationAbortController.value = controller
    const signal = controller.signal
    isGenerating.value = true
    generationSuccesses.value = []
    generationFailures.value = []
    const picked = props.variablesOptions.filter(
        (o) => selectedByVariable.value[o.value]
    )
    const initialLines: Record<string, GenerationLineStatus> = {}
    for (const o of picked) {
        initialLines[o.value] = "loading"
    }
    generationLineByVariable.value = initialLines
    const settled = await Promise.all(
        picked.map(async (option) => {
            const result = await tryFetchGeneratedPoints(option, signal)
            setGenerationLineStatus(
                option.value,
                result.ok ? "success" : "error"
            )
            return { option, result }
        })
    )
    if (signal.aborted) {
        isGenerating.value = false
        generationAbortController.value = null
        generationLineByVariable.value = {}
        return
    }
    for (const { option, result } of settled) {
        if (result.ok) {
            generationSuccesses.value.push({
                variable: option.value,
                displayName: option.name,
                freq: result.freq,
                points: result.points,
            })
        } else {
            const reason =
                result.error instanceof Error &&
                result.error.message === "NO_PROFIL_CONFIG"
                    ? t("alarmBulk.noProfilConfig")
                    : getGenerationFailureMessage(result.error)
            generationFailures.value.push({
                variable: option.value,
                displayName: option.name,
                reason,
            })
        }
    }
    isGenerating.value = false
    generationAbortController.value = null
    step.value = "review"
}

/**
 * Retour à l’étape de sélection sans conserver les résultats de génération.
 *
 * @returns void
 */
function onBackToSelection(): void {
    step.value = "select"
    generationSuccesses.value = []
    generationFailures.value = []
    generationLineByVariable.value = {}
}

/**
 * Enregistre les seuils générés (profils uniquement, sans activer la surveillance).
 *
 * @returns void
 */
async function onPersistGenerated(): Promise<void> {
    if (!getHasSuccessesToPersist.value || isPersisting.value) return
    persistAbortController.value?.abort()
    const controller = new AbortController()
    persistAbortController.value = controller
    const signal = controller.signal
    isPersisting.value = true
    const target =
        props.type === "building"
            ? { scope: "building" as const, id: props.itemId }
            : { scope: "source" as const, id: props.itemId }
    try {
        for (const row of generationSuccesses.value) {
            await alarmeStore.addThreshold(target, row.variable, row.points, {
                signal,
            })
        }
        toast.add({
            group: "notifications",
            severity: "success",
            summary: t("alarmBulk.title"),
            detail: t("alarmBulk.persistSuccess", {
                count: generationSuccesses.value.length,
            }),
            life: 5000,
        })
        visible.value = false
    } catch (err) {
        if (isCancel(err)) {
            return
        }
        const msg =
            err instanceof ServiceError
                ? err.message
                : t("alarmBulk.persistError")
        toast.add({
            group: "notifications",
            severity: "error",
            summary: t("alarmBulk.title"),
            detail: msg,
            life: 8000,
        })
    } finally {
        isPersisting.value = false
        persistAbortController.value = null
    }
}

/**
 * Ferme la modale (bouton Annuler / fermer).
 *
 * @returns void
 */
function onClose(): void {
    generationAbortController.value?.abort()
    persistAbortController.value?.abort()
    visible.value = false
}
</script>

<template>
    <!-- ================================ -->
    <!-- DIALOG — GÉNÉRATION GROUPÉE D’ALARMES -->
    <!-- ================================ -->
    <BaseDialog
        v-model:visible="visible"
        modal
        :header="getDialogHeader"
        class="alarm-bulk-generate-dialog w-[min(100%,36rem)] max-h-[90vh]"
        :breakpoints="{ '575px': '95vw' }"
        dismissable-mask
    >
        <!-- ================================ -->
        <!-- CONTENU — SÉLECTION -->
        <!-- ================================ -->
        <div v-if="step === 'select'" class="flex flex-col gap-4 py-1">
            <p class="m-0 text-sm text-surface-600">
                {{ t("alarmBulk.intro") }}
            </p>
            <div class="flex flex-wrap gap-2">
                <BaseButton
                    type="button"
                    size="small"
                    text
                    severity="secondary"
                    :disabled="!variablesOptions.length || isGenerating"
                    :label="t('alarmBulk.selectAll')"
                    @click="onToggleSelectAll(true)"
                />
                <BaseButton
                    type="button"
                    size="small"
                    text
                    severity="secondary"
                    :disabled="!variablesOptions.length || isGenerating"
                    :label="t('alarmBulk.selectNone')"
                    @click="onToggleSelectAll(false)"
                />
            </div>
            <div
                class="max-h-[min(320px,45vh)] overflow-auto rounded-md border border-surface-200"
            >
                <ul
                    class="m-0 list-none divide-y divide-surface-200 p-0 overflow-hidden"
                >
                    <li
                        v-for="opt in variablesOptions"
                        :key="opt.value"
                        class="flex items-center gap-2 py-2.5 pl-3 pr-1.5"
                    >
                        <BaseCheckbox
                            :model-value="selectedByVariable[opt.value]"
                            binary
                            :input-id="`bulk-alarm-${opt.value}`"
                            :disabled="isGenerating"
                            @update:model-value="
                                onVariableCheck(opt.value, Boolean($event))
                            "
                        />
                        <label
                            class="min-w-0 flex-1 cursor-pointer text-sm leading-snug text-surface-900"
                            :class="{
                                'cursor-not-allowed opacity-60': isGenerating,
                            }"
                            :for="`bulk-alarm-${opt.value}`"
                        >
                            {{ opt.name }}
                        </label>
                        <div
                            v-if="getShowBulkFreqSelector(opt.value)"
                            class="flex shrink-0 flex-wrap justify-end gap-1"
                            role="group"
                            :aria-label="`${opt.name} — fréquence`"
                        >
                            <BaseButton
                                v-for="(fo, fi) in getBulkFreqButtonOptions(
                                    opt.value
                                )"
                                :key="`bulk-freq-${opt.value}-${fi}`"
                                type="button"
                                size="small"
                                :outlined="
                                    freqByVariable[opt.value] !== fo.value
                                "
                                :severity="
                                    freqByVariable[opt.value] === fo.value
                                        ? 'primary'
                                        : 'secondary'
                                "
                                :disabled="isGenerating"
                                :label="fo.label"
                                @click="onBulkVariableFreq(opt.value, fo.value)"
                            />
                        </div>
                        <div
                            v-if="
                                isGenerating ||
                                generationLineByVariable[opt.value]
                            "
                            class="ml-auto flex h-[22px] w-[22px] shrink-0 items-center justify-center self-center"
                            aria-hidden="true"
                        >
                            <BaseProgressSpinner
                                v-if="
                                    generationLineByVariable[opt.value] ===
                                    'loading'
                                "
                                class="h-4 w-4"
                                stroke-width="4"
                            />
                            <i
                                v-else-if="
                                    generationLineByVariable[opt.value] ===
                                    'success'
                                "
                                class="pi pi-check text-[0.9rem] leading-none text-green-600"
                            />
                            <i
                                v-else-if="
                                    generationLineByVariable[opt.value] ===
                                    'error'
                                "
                                class="pi pi-times text-[0.9rem] leading-none text-red-500"
                            />
                        </div>
                    </li>
                </ul>
            </div>
        </div>

        <!-- ================================ -->
        <!-- CONTENU — RÉSULTATS -->
        <!-- ================================ -->
        <div
            v-else
            class="flex max-h-[min(520px,70vh)] flex-col gap-4 overflow-y-auto py-1"
        >
            <div v-if="generationSuccesses.length" class="flex flex-col gap-2">
                <h3 class="m-0 text-sm font-semibold text-surface-800">
                    {{ t("alarmBulk.successesTitle") }}
                </h3>
                <ul
                    class="m-0 list-none space-y-1 rounded-md border border-surface-200 p-0"
                >
                    <li
                        v-for="row in generationSuccesses"
                        :key="row.variable"
                        class="flex flex-wrap justify-between gap-2 border-b border-surface-100 px-3 py-2 text-sm last:border-b-0"
                    >
                        <span class="font-medium text-surface-900">{{
                            row.displayName
                        }}</span>
                        <span class="text-surface-600">{{
                            formatFreqLabel(row.freq)
                        }}</span>
                    </li>
                </ul>
            </div>

            <div v-if="generationFailures.length" class="flex flex-col gap-2">
                <h3 class="m-0 text-sm font-semibold text-surface-800">
                    {{ t("alarmBulk.failuresTitle") }}
                </h3>
                <p class="m-0 text-xs text-surface-600">
                    {{ t("alarmBulk.failuresHint") }}
                </p>
                <div
                    class="overflow-x-auto rounded-md border border-surface-200"
                >
                    <table
                        class="w-full min-w-[280px] border-collapse text-left text-sm"
                    >
                        <thead>
                            <tr
                                class="border-b border-surface-200 bg-surface-50"
                            >
                                <th
                                    class="px-3 py-2 font-semibold text-surface-800"
                                >
                                    {{ t("alarmBulk.columnVariable") }}
                                </th>
                                <th
                                    class="px-3 py-2 font-semibold text-surface-800"
                                >
                                    {{ t("alarmBulk.columnReason") }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="fail in generationFailures"
                                :key="fail.variable"
                                class="border-b border-surface-100 last:border-b-0"
                            >
                                <td
                                    class="px-3 py-2 align-top text-surface-900"
                                >
                                    {{ fail.displayName }}
                                </td>
                                <td
                                    class="px-3 py-2 align-top text-surface-700"
                                >
                                    {{ fail.reason }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- ================================ -->
        <!-- PIED — ACTIONS -->
        <!-- ================================ -->
        <template #footer>
            <div class="flex w-full flex-wrap items-center justify-end gap-2">
                <template v-if="step === 'select'">
                    <BaseButton
                        type="button"
                        outlined
                        severity="secondary"
                        :label="t('alarmBulk.cancel')"
                        @click="onClose"
                    />
                    <BaseButton
                        type="button"
                        :loading="isGenerating"
                        :disabled="!getCanGenerate"
                        :label="t('alarmBulk.generate')"
                        @click="onGenerate"
                    />
                </template>
                <template v-else>
                    <BaseButton
                        type="button"
                        outlined
                        severity="secondary"
                        :label="t('alarmBulk.backToSelection')"
                        @click="onBackToSelection"
                    />
                    <BaseButton
                        type="button"
                        outlined
                        severity="secondary"
                        :label="t('alarmBulk.close')"
                        @click="onClose"
                    />
                    <BaseButton
                        type="button"
                        :loading="isPersisting"
                        :disabled="!getHasSuccessesToPersist || isPersisting"
                        :label="t('alarmBulk.addGenerated')"
                        @click="onPersistGenerated"
                    />
                </template>
            </div>
        </template>
    </BaseDialog>
</template>
